/**
 * markdownRender - presentational helpers for rendering answer markdown on the
 * Knowledge Graph page. Extracted from KnowledgeGraphPage.jsx in Phase 5F -
 * IJ-462 of umbrella IJ-150 (composition-root handler shrink).
 *
 * markdownComponents is a byte-verbatim lift (pure, stateless component map).
 * renderContentWithCitations is verbatim apart from taking onCitationClick as
 * an argument in place of the parent's closed-over handler, so the parent can
 * keep setCitationModal. Both produce identical output.
 */
import React from 'react';

export const markdownComponents = {
  h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-white">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-bold mb-3 text-white">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-bold mb-2 text-white">{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-bold mb-2 text-white">{children}</h4>,
  ul: ({ children }) => <ul className="list-disc pl-4 mb-4 space-y-1 marker:text-cyan-300">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-4 mb-4 space-y-1 marker:text-cyan-300 marker:font-bold">{children}</ol>,
  li: ({ children }) => <li className="text-gray-200 leading-relaxed ml-2 text-lg">{children}</li>,
  p: ({ children }) => <p className="mb-3 text-gray-200 leading-relaxed text-lg">{children}</p>,
  strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
  em: ({ children }) => <em className="text-cyan-300 italic">{children}</em>,
  code: ({ children }) => <code className="bg-gray-800 text-gray-100 px-2 py-1 rounded text-sm font-mono">{children}</code>,
  pre: ({ children }) => <pre className="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono mb-4 whitespace-pre-wrap">{children}</pre>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-blue-500 pl-4 italic mb-4 text-gray-300">{children}</blockquote>,
  hr: () => <hr className="border-gray-300 my-6" />,
  table: ({ children }) => <div className="overflow-x-auto mb-4"><table className="min-w-full border-collapse border border-gray-400">{children}</table></div>,
  th: ({ children }) => <th className="border border-gray-600 px-3 py-2 bg-white/10 font-semibold text-left text-white">{children}</th>,
  td: ({ children }) => <td className="border border-gray-400 px-3 py-2 text-gray-200">{children}</td>,
};

export const renderContentWithCitations = (content, citationMap, onCitationClick) => {
  if (!content || !citationMap || Object.keys(citationMap).length === 0) {
    return content;
  }

  // Match one-or-more opening brackets, digits, one-or-more closing brackets.
  const citationRegex = /(\[+\d+\]+)/g;
  const parts = content.split(citationRegex);
  return parts.map((part, index) => {
    const match = part.match(/\[+(\d+)\]+/);
    if (match) {
      const citationNum = match[1];
      const citation = citationMap[citationNum];
      if (citation) {
        return (
          <span
            key={index}
            className="inline-flex items-center px-1.5 py-0.5 mx-0.5 text-xs font-semibold rounded-full cursor-pointer transition-all hover:scale-110"
            style={{ 
              backgroundColor: 'rgba(0, 212, 255, 0.2)', 
              color: '#0891b2',
              border: '1px solid rgba(0, 212, 255, 0.4)'
            }}
            onClick={() => onCitationClick(citationNum, citationMap)}
            title={`Source: ${citation.filename} - Click to view excerpt`}
          >
            {citationNum}
          </span>
        );
      }
    }
    return part;
  });
};
